import React, { useEffect, useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect, matchPath } from 'react-router-dom';
import { routes as originRoutes } from '../../configs/routes';
import { ActionContext, SessionContext, LayoutContext, RouterContext } from './MainContainer';
import { APP_URL, STORAGE_KEYS, USER_CONFIG } from '../../configs/AppConfig';
import find from 'lodash/find';
import debounce from 'lodash/debounce';

import { getMenuRoutes } from '../../state/route/routeSelector';
import { connect } from 'react-redux';
import ParentContainerRoute from './ParentContainerRoute';
import { makeStyles, useTheme } from '@material-ui/styles';
import DefaultAppBar from '../core/DefaultAppBar';
import { Typography } from '@material-ui/core';
import TranslateMessage from '../common/TranslateMessage/TranslateMessage';
import AppDrawer from '../core/AppDrawer';
import { getEndUserCompanyDetail } from '../../state/company/companySelector';
import DefaultLayout from './DefaultLayout';
import StorageHelper from '../../utils/StorageHelper';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex'
  },
  toolbar: {
    minHeight: '45px'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: theme.spacing(1),
    width: '100%',
    transition: 'all 0.3s ease-out, transform 0.3s ease-out'
  },
  header: {
    marginBottom: theme.spacing(1)
  },
  footer: {
    marginTop: theme.spacing(3),
    flexShrink: 0
  }
}));

const AuthenticatedRoute = props => {
  const {
    routes,
    session: { isAuthenticated, user },
    actions: {
      sessionActions: { cacheRoutePath, logout, checkActivity },
      companyActions: { loadEndUserCompanyDetail }
    },
    router: { location },
    companyDetail
  } = props;
  const theme = useTheme();
  const width = window.innerWidth;
  const themeWidth = theme.breakpoints.values;
  const classes = useStyles();
  const [drawerValue, setDrawerValue] = useState('normal');

  /**
   * Toggle drawer to various size
   */
  const toggleDrawer = () => {
    if (width >= themeWidth.md) {
      if (drawerValue === 'normal' || drawerValue === 'mobile') {
        setDrawerValue('mini');
      } else {
        setDrawerValue('normal');
      }
    } else {
      if (drawerValue === 'normal' || drawerValue === 'mini') {
        setDrawerValue('mobile');
      } else {
        setDrawerValue('mini');
      }
    }
  };

  const setActivityDate = (event) => {
    const timeRange = StorageHelper.getLocalItem(STORAGE_KEYS.autoLogout);
    if (timeRange && parseInt(timeRange) > 0) {
      StorageHelper.setLocalItem(STORAGE_KEYS.lastActivity, new Date().toISOString());
      checkActivityDebounce(event);
    }
  };

  const checkActivityDebounce = debounce(event => {
    checkActivity();
  }, 3000);

  let interval;

  const doAutoLogout = () => {
    const timeRange = StorageHelper.getLocalItem(STORAGE_KEYS.autoLogout);
    if (isAuthenticated && user.roleLevel === USER_CONFIG.COMPANY_ADMIN.roleLevel && timeRange && parseInt(timeRange) > 0) {
      window.onload = setActivityDate;
      window.onmousemove = setActivityDate;
      window.onmousedown = setActivityDate;
      window.ontouchstart = setActivityDate;
      window.onclick = setActivityDate;
      window.onkeydown = setActivityDate;
      interval = setInterval(() => {
        const timeRange = StorageHelper.getLocalItem(STORAGE_KEYS.autoLogout);
        const actDate = StorageHelper.getLocalItem(STORAGE_KEYS.lastActivity);
        if (isAuthenticated && actDate && timeRange && parseInt(timeRange) > 0) {
          const activityDate = new Date(actDate);
          const current = new Date();
          const diffMs = (current - activityDate); // milliseconds between now & Christmas
          const diffMins = Math.floor(((diffMs % 86400000) % 3600000) / 60000); // minutes
          if (diffMins >= parseInt(timeRange)) {
            logout();
          }
        }
      }, 1000);
    }
  };

  useEffect(() => {
    // Load company detail on page load
    // Is preview is true by default
    if (isAuthenticated && user.roleLevel === USER_CONFIG.COMPANY_ADMIN.roleLevel) {
      loadEndUserCompanyDetail();
    }
    // Check auto logout
    doAutoLogout();

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, []);

  useEffect(() => {
    if (companyDetail && companyDetail.favicon) {
      const favicon = APP_URL.FAVICON_UPLOAD_PATH + '/' + companyDetail.favicon;
      const favElement = document.getElementById('favicon');
      favElement.href = favicon;
    }
  }, [companyDetail]);

  /**
   * Watch layout changing
   */
  useLayoutEffect(() => {
    /**
     * Update drawer size
     * Func updateSize
     */
    function updateSize () {
      const width = window.innerWidth;
      if (width < themeWidth.md) {
        setDrawerValue('mobile');
      } else if (width < themeWidth.lg) {
        setDrawerValue('mini');
      } else {
        setDrawerValue('normal');
      }
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    // Clear event listener on page will destroyed
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  if (!isAuthenticated) {
    cacheRoutePath();
    return <Redirect to={originRoutes.unauthorizedRoutes.routes[0].path} push />;
  } else {
    const userConfig = find(USER_CONFIG, { roleLevel: user.roleLevel });

    const customRoutes = routes.filter(el => el.layoutId.startsWith(originRoutes.authorizedRoutes.name, 0));

    const route = find(customRoutes, route => {
      return matchPath(location.pathname, { path: route.path, exact: true }) &&
        route.accessRole && route.accessRole.indexOf(user.roleLevel) > -1 && route;
    });
    if (userConfig && route) {
      const isPreview = location.pathname !== '/company/preview' && location.pathname.includes('preview');
      if (!isPreview) {
        return (
          <div className={classes.root}>
            <DefaultAppBar location={location} toggleDrawer={toggleDrawer}/>
            <AppDrawer location={location} drawerValue={drawerValue}/>
            <main className={classes.content}>
              <div className={classes.toolbar} />
              <div className={classes.header}>
                <Typography variant="h6"><TranslateMessage id={route.title} /></Typography>
              </div>
              <div style={{ flex: '1 0 auto' }}>
                <ParentContainerRoute routes={customRoutes} />
              </div>
              <div className={classes.footer}>
                <Typography variant="body2">
                  &copy;{new Date().getFullYear()} iDOC K.K.
                </Typography>
              </div>
            </main>
          </div>
        );
      } else {
        let basePath = '/';
        if (location.pathname.includes('preview')) {
          basePath = '/preview';
        }
        let isAgree = StorageHelper.getSessionItem(STORAGE_KEYS.disclosureAgreement);
        let type = 'disclosure';
        if (location.pathname && location.pathname.includes('patient')) {
          isAgree = StorageHelper.getSessionItem(STORAGE_KEYS.patientAgreement);
          type = 'patient';
        }
        if (!isAgree && location.pathname.includes(type)) {
          cacheRoutePath();
          return <Redirect to={{ pathname: basePath, search: `?type=${type}` }}/>;
        }
        if (location.pathname.includes('viewer')) {
          return (
            <ParentContainerRoute routes={customRoutes} />
          );
        }
        return (
          <DefaultLayout useAppBar={location.pathname.includes('faq')}>
            <ParentContainerRoute routes={customRoutes} />
          </DefaultLayout>
        );
      }
    } else {
      return <Redirect to={userConfig.landingPage} push />;
    }
  }
};

AuthenticatedRoute.propsTypes = {
  session: PropTypes.object.isRequired,
  layout: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
  router: PropTypes.node.isRequired,
  routes: PropTypes.array.isRequired,
  companyDetail: PropTypes.object
};

const mapStateToProps = (state) => {
  return {
    menuRoutes: getMenuRoutes(state),
    companyDetail: getEndUserCompanyDetail(state)
  };
};

export default connect(mapStateToProps)(props => (
  <RouterContext.Consumer>
    {router => (
      <ActionContext.Consumer>
        {actions => (
          <SessionContext.Consumer>
            {session => (
              <LayoutContext.Consumer>
                {layout => (
                  <AuthenticatedRoute
                    {...props}
                    session={session}
                    router={router}
                    layout={layout}
                    actions={actions}
                  />
                )}
              </LayoutContext.Consumer>
            )}
          </SessionContext.Consumer>
        )}
      </ActionContext.Consumer>
    )}
  </RouterContext.Consumer>
));
